import type { IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { type InclusionOption, inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";

const TRACKING_CATEGORY = "industry taxonomy v2";

const suggestionModeTextMap: Readonly<Record<InclusionOption, string>> = {
    [inclusionOptionMap.includeOnly]: "include",
    [inclusionOptionMap.excludeOnly]: "exclude",
    [inclusionOptionMap.any]: "any",
};

type ScoredNode = { readonly id: IndustryNodeId; readonly score: number };

const roundScore = (score: number): number => Math.round(score * 100) / 100;

const suggestionEventName = (action: string, inclusion: InclusionOption) =>
    createTrackingEventName(action, suggestionModeTextMap[inclusion]);

/**
 * Kept out of the demo-mode service: its close event is modelled as `demo mode / exit`, which would be a
 * false report for a user who is opted in and therefore never in a demo.
 */
const createIndustryTrackingService = (track: TrackFn) => {
    return {
        trackLaunchBannerCtaClick() {
            track(TRACKING_CATEGORY, "click", "launch banner cta");
        },
        trackLaunchBannerDismiss() {
            track(TRACKING_CATEGORY, "click", "launch banner dismiss");
        },
        trackImpactReportOpen(source: "banner" | "email" = "banner") {
            track(
                TRACKING_CATEGORY,
                "click",
                source === "email" ? "impact report open from email" : "impact report open",
            );
        },
        trackImpactReportEnroll() {
            track(TRACKING_CATEGORY, "click", "impact report enroll");
        },
        trackImpactReportEnrollRetry() {
            track(TRACKING_CATEGORY, "click", "impact report enroll retry");
        },
        trackImpactReportDismiss() {
            track(TRACKING_CATEGORY, "click", "impact report dismiss");
        },
        trackImpactReportContactUs() {
            track(TRACKING_CATEGORY, "click", "impact report contact us");
        },
        /** A settled state, not a click, so the account-migration funnel can measure completion. */
        trackImpactReportEnrollSuccess() {
            track(TRACKING_CATEGORY, "view", "impact report enrolled");
        },
        trackImpactReportViewLeadGeneration() {
            track(TRACKING_CATEGORY, "click", "enroll success view lead generation");
        },
        trackImpactReportCrmSettings() {
            track(TRACKING_CATEGORY, "click", "enroll success crm settings");
        },
        /**
         * Suggested industries carry nothing about the page, tab or demo: every event already has the page
         * context, and funnels recover the rest. Node ids travel as JSON attributes, never in the event name,
         * because they are opaque paths that may contain the `/` separator and commas.
         */
        trackSuggestionsImpression(
            inclusion: InclusionOption,
            {
                appliedNodeIds,
                suggestions,
            }: { appliedNodeIds: readonly IndustryNodeId[]; suggestions: readonly ScoredNode[] },
        ) {
            track(TRACKING_CATEGORY, "view", suggestionEventName("suggested industries", inclusion), {
                mode: suggestionModeTextMap[inclusion],
                appliedNodeIds: JSON.stringify(appliedNodeIds),
                suggestedNodeIds: JSON.stringify(suggestions.map(({ id }) => id)),
                affinityScores: JSON.stringify(suggestions.map(({ score }) => roundScore(score))),
            });
        },
        trackSuggestionAdd(
            inclusion: InclusionOption,
            { nodeId, score, position }: { nodeId: IndustryNodeId; score: number; position: number },
        ) {
            track(TRACKING_CATEGORY, "click", suggestionEventName("add suggested industry", inclusion), {
                mode: suggestionModeTextMap[inclusion],
                suggestedNodeId: nodeId,
                affinityScore: roundScore(score),
                position,
            });
        },
        trackSuggestionDismiss(
            inclusion: InclusionOption,
            { nodeId, replacementNodeId }: { nodeId: IndustryNodeId; replacementNodeId: IndustryNodeId | undefined },
        ) {
            track(TRACKING_CATEGORY, "click", suggestionEventName("dismiss suggested industry", inclusion), {
                mode: suggestionModeTextMap[inclusion],
                dismissedNodeId: nodeId,
                replacementNodeId: replacementNodeId ?? "none",
            });
        },
    };
};

export default createIndustryTrackingService;
